/**
 * #calls channel renderer (batch 6b).
 *
 * Signal-style inline call-history log. Rows are pulled from GET /api/calls
 * and updated live via SSE (`call_incoming` / `call_answered` / `call_ended` /
 * `call_updated`). Each row shows an arrow icon (↙ inbound / ↗ outbound),
 * the counterparty, a status summary, and the duration / end reason.
 *
 * A "Dial" button in the header opens the dialer modal.
 */

import { get } from '../api';
import { state } from '../state';
import { realtime, channelTopic, type CallRow, type RealtimeEvent } from '../realtime';
import { openDialer } from '../components/dialer';
import { ensureCallSubscription } from '../components/call-session';
import { fmtCallTime, fmtFull } from '../util/time';
import { renderAutomation } from './calls-automation';

let rows: CallRow[] = [];
let currentTopic: string | null = null;
let unsubRealtime: (() => void) | null = null;
let rerenderTimer: ReturnType<typeof setTimeout> | null = null;

type CallFilter = 'all' | 'inbound' | 'outbound' | 'missed' | 'rejected';
let activeFilter: CallFilter = 'all';

type CallsView = 'log' | 'automation';
let callsView: CallsView = 'log';

function isMissedCall(r: CallRow): boolean {
  return r.status === 'missed' || (r.direction === 'in' && (r.status === 'ended' || r.status === 'no_answer' || r.status === 'busy') && !r.answered_at);
}

function filterRows(all: CallRow[], f: CallFilter): CallRow[] {
  switch (f) {
    case 'inbound': return all.filter(r => r.direction === 'in');
    case 'outbound': return all.filter(r => r.direction === 'out');
    case 'missed': return all.filter(r => isMissedCall(r));
    case 'rejected': return all.filter(r => r.status === 'rejected');
    default: return all;
  }
}

export function cleanupCalls(): void {
  if (unsubRealtime) { unsubRealtime(); unsubRealtime = null; }
  if (currentTopic) { realtime.unsubscribeChannel(currentTopic); currentTopic = null; }
  if (rerenderTimer) { clearTimeout(rerenderTimer); rerenderTimer = null; }
  rows = [];
}

export async function renderCalls(container: HTMLElement): Promise<void> {
  cleanupCalls();

  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) {
    container.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Select a server to view calls.</div>';
    return;
  }

  // Two-mode shell: the call-history log and the Automation panel (scheduled
  // reminder calls + 137 note-taking config). Switching modes tears down the
  // call-log realtime subscription via cleanupCalls().
  container.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div style="padding:8px 16px;border-bottom:1px solid var(--border);display:flex;gap:6px;align-items:center;font-family:var(--font-sans)">
        <button class="btn btn-sm" data-cmode="log" style="font-size:12px;padding:4px 14px">Call log</button>
        <button class="btn btn-sm" data-cmode="automation" style="font-size:12px;padding:4px 14px">Automation</button>
      </div>
      <div id="calls-mode-content" style="flex:1;min-height:0;display:flex;flex-direction:column"></div>
    </div>
  `;
  const syncModeButtons = (): void => {
    container.querySelectorAll<HTMLButtonElement>('[data-cmode]').forEach(b => {
      const on = b.dataset['cmode'] === callsView;
      b.classList.toggle('btn-primary', on);
      b.classList.toggle('btn-secondary', !on);
    });
  };
  container.querySelectorAll<HTMLButtonElement>('[data-cmode]').forEach(btn => {
    btn.addEventListener('click', () => {
      const next: CallsView = btn.dataset['cmode'] === 'automation' ? 'automation' : 'log';
      if (next === callsView) return;
      callsView = next;
      cleanupCalls();
      syncModeButtons();
      void renderActiveMode(sid);
    });
  });
  syncModeButtons();
  await renderActiveMode(sid);
}

async function renderActiveMode(sid: string): Promise<void> {
  const host = document.getElementById('calls-mode-content');
  if (!host) return;
  if (callsView === 'automation') {
    await renderAutomation(host, sid);
  } else {
    await renderCallLog(host, sid);
  }
}

async function renderCallLog(host: HTMLElement, sid: string): Promise<void> {
  const filterTabs: { key: CallFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'inbound', label: 'Inbound' },
    { key: 'outbound', label: 'Outbound' },
    { key: 'missed', label: 'Missed' },
    { key: 'rejected', label: 'Rejected' },
  ];

  host.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div style="padding:10px 16px;border-bottom:1px solid var(--border);display:flex;gap:10px;align-items:center;font-family:var(--font-sans);font-size:13px;flex-wrap:wrap">
        <div style="font-weight:600;color:var(--fg)">Calls</div>
        <div id="calls-count" style="color:var(--fg-muted)">loading…</div>
        <div id="calls-filters" style="display:flex;gap:2px;margin-left:8px">
          ${filterTabs.map(t => `<button class="btn btn-sm ${activeFilter === t.key ? 'btn-primary' : 'btn-secondary'}" data-cf="${t.key}" style="font-size:11px;padding:3px 10px">${t.label}</button>`).join('')}
        </div>
        <button id="calls-dial" style="margin-left:auto;background:var(--accent, #16a34a);border:none;color:var(--fg-on-accent, #fff);padding:6px 14px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;display:inline-flex;gap:6px;align-items:center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--fg-on-accent, #fff)"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 0 0-1.02.24l-2.2 2.2a15.1 15.1 0 0 1-6.59-6.58l2.2-2.21a1 1 0 0 0 .25-1.02A11.4 11.4 0 0 1 8.5 4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1 17 17 0 0 0 17 17 1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1z"/></svg>
          Dial
        </button>
      </div>
      <div id="calls-list" style="flex:1;min-height:0;overflow-y:auto"></div>
    </div>
  `;
  document.getElementById('calls-dial')?.addEventListener('click', () => openDialer());
  host.querySelectorAll<HTMLButtonElement>('[data-cf]').forEach(btn => {
    btn.addEventListener('click', () => {
      activeFilter = (btn.dataset['cf'] || 'all') as CallFilter;
      host.querySelectorAll<HTMLButtonElement>('[data-cf]').forEach(b => {
        b.classList.toggle('btn-primary', b.dataset['cf'] === activeFilter);
        b.classList.toggle('btn-secondary', b.dataset['cf'] !== activeFilter);
      });
      const listEl = document.getElementById('calls-list');
      const countEl = document.getElementById('calls-count');
      if (listEl) renderList(listEl, countEl);
    });
  });

  const listEl = document.getElementById('calls-list');
  const countEl = document.getElementById('calls-count');
  if (!listEl) return;

  try {
    rows = await get<CallRow[]>(`/api/calls?server_id=${encodeURIComponent(sid)}&limit=200`);
  } catch {
    rows = [];
  }
  renderList(listEl, countEl);

  const topic = channelTopic(sid, 'calls');
  currentTopic = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);
  ensureCallSubscription();
  unsubRealtime = realtime.on((ev: RealtimeEvent) => {
    if (ev.type !== 'call_incoming' && ev.type !== 'call_answered' && ev.type !== 'call_ended' && ev.type !== 'call_updated') return;
    if (ev.channel_id !== topic) return;
    const row = ev.call;
    const idx = rows.findIndex(r => r.id === row.id);
    if (idx >= 0) rows[idx] = row;
    else rows = [row, ...rows];
    scheduleRerender(listEl, countEl);
  });
}

function scheduleRerender(listEl: HTMLElement, countEl: HTMLElement | null): void {
  if (rerenderTimer) return;
  rerenderTimer = setTimeout(() => {
    rerenderTimer = null;
    renderList(listEl, countEl);
  }, 60);
}

function renderList(listEl: HTMLElement, countEl: HTMLElement | null): void {
  const filtered = filterRows(rows, activeFilter);
  const sorted = [...filtered].sort((a, b) => b.started_at.localeCompare(a.started_at));
  if (countEl) countEl.textContent = `${sorted.length} call${sorted.length === 1 ? '' : 's'}${activeFilter !== 'all' ? ` (${activeFilter})` : ''}`;
  if (sorted.length === 0) {
    listEl.innerHTML = `
      <div style="padding:48px 20px;text-align:center;color:var(--fg-muted);font-family:var(--font-sans)">
        <div style="font-size:18px;margin-bottom:8px">No calls yet</div>
        <div style="font-size:13px">Dial a number above or answer an incoming call when the modem rings.</div>
      </div>
    `;
    return;
  }
  listEl.innerHTML = sorted.map(renderRow).join('');
}

function renderRow(r: CallRow): string {
  const hasPhone = !!(r.counterparty_phone && r.counterparty_phone.trim());
  const hasName = !!(r.counterparty_name && r.counterparty_name.trim());
  // Display precedence: saved name > raw phone > "Unknown caller" fallback.
  // When the Pi URC parser didn't carry CLIP (e.g. caller blocked CID, or
  // +CLIP URC arrived after status transitioned — guarded against on the
  // backend now) we still render a meaningful row instead of a blank one.
  const cp = hasName
    ? r.counterparty_name!
    : hasPhone
      ? r.counterparty_phone
      : 'Unknown caller';
  const sub = hasName && hasPhone ? r.counterparty_phone : '';
  const when = prettyTime(r.started_at);
  const whenFull = fullTime(r.started_at);
  const statusLabel = statusLine(r);
  const missed = isMissedCall(r);
  const rejected = r.status === 'rejected';
  const inbound = r.direction === 'in';
  const arrow = rejected
    ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="#dc2626"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z"/></svg>'
    : inbound
      ? (missed
          ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="#dc2626"><path d="M20 5.41 18.59 4 7 15.59V9H5v10h10v-2H8.41z"/></svg>'
          : '<svg width="14" height="14" viewBox="0 0 24 24" fill="#16a34a"><path d="M20 5.41 18.59 4 7 15.59V9H5v10h10v-2H8.41z"/></svg>')
      : '<svg width="14" height="14" viewBox="0 0 24 24" fill="#3b82f6"><path d="m5 17.59 11.59-11.58H10v-2h10v10h-2V7.41L6.41 19z"/></svg>';
  return `
    <div class="calls-row" data-id="${esc(r.id)}" style="padding:12px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:12px;font-family:var(--font-sans)">
      <div style="width:36px;height:36px;border-radius:50%;background:var(--bg-elev);display:flex;align-items:center;justify-content:center;color:var(--fg-muted);font-size:15px;font-weight:600;flex:0 0 auto">${esc((cp || '?').charAt(0).toUpperCase())}</div>
      <div style="flex:1;min-width:0;display:flex;flex-direction:column">
        <div style="display:flex;align-items:baseline;gap:8px">
          <span style="color:${missed || rejected ? '#dc2626' : 'var(--fg)'};font-weight:600;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(cp)}</span>
          ${sub ? `<span style="color:var(--fg-muted);font-size:12px;font-family:var(--font-mono,monospace)">${esc(sub)}</span>` : ''}
        </div>
        <div style="color:var(--fg-muted);font-size:12px;display:flex;align-items:center;gap:6px">
          ${arrow}
          <span>${esc(statusLabel)}</span>
        </div>
      </div>
      <div style="color:var(--fg-muted);font-size:12px;font-family:var(--font-mono,monospace);flex:0 0 auto" title="${esc(whenFull)}">${esc(when)}</div>
    </div>
  `;
}

function statusLine(r: CallRow): string {
  if (r.status === 'ringing') return r.direction === 'in' ? 'Ringing…' : 'Calling…';
  if (r.status === 'active') return 'In progress';
  if (r.status === 'missed') return 'Missed';
  if (r.status === 'busy') return 'Busy';
  if (r.status === 'no_answer') return 'No answer';
  if (r.status === 'failed') return 'Failed';
  if (r.status === 'rejected') return r.end_reason === 'auto_rejected' ? 'Auto-rejected' : 'Blocked';
  if (r.duration_s && r.duration_s > 0) {
    return formatDuration(r.duration_s);
  }
  if (!r.answered_at) return r.direction === 'in' ? 'Missed' : 'No answer';
  return 'Ended';
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function prettyTime(iso: string): string {
  // Always renders in the operator timezone (Australia/Sydney) — see
  // util/time.ts. Kept as a thin wrapper so call sites don't change.
  return fmtCallTime(iso);
}

/** Full tooltip time, used on hover. */
function fullTime(iso: string): string {
  return fmtFull(iso);
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}
